import React, { useState } from 'react';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const BannerForm = () => {
    const [formData, setFormData] = useState({
        stateCode: '',
        languageId: '',
        heading1: 'Aadhaar enabled Public Distribution System -AePDS',
        heading2: 'Food, Civil Supplies and Consumer Affairs Department',
        heading3: '',
        stateName: '',
        pageTitle: '',
        designedBy: '',
        deptUrl: '',
        copyright: ''
    });
    
    const [leftImage, setLeftImage] = useState(null);
    const [leftImage_1, setLeftImage_1] = useState(null);
    const [rightImage, setRightImage] = useState(null);
    const [rightImage_1, setRightImage_1] = useState(null);
    const [nicLogo, setNicLogo] = useState(null);
    const [leftPreview, setLeftPreview] = useState(null);
    const [leftPreview_1, setLeftPreview_1] = useState(null);
    const [rightPreview, setRightPreview] = useState(null);
    const [rightPreview_1, setRightPreview_1] = useState(null);
    const [nicLogoPreview, setNicLogoPreview] = useState(null);
    const [message, setMessage] = useState(''); 
    const [errors, setErrors] = useState({
        leftImage: '',
        leftImage_1: '',
        rightImage: '',
        rightImage_1: '',
        nicLogo: ''
    });
    const [showPreview, setShowPreview] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [loading, setLoading] = useState(false);

    
    const MAX_FILE_SIZE = 250 * 1024; // 250KB in bytes
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Validate stateCode (up to 2 digits)
    if (name === 'stateCode') {
        if (!/^\d{0,2}$/.test(value)) {
            setMessage('State Code must be up to 2 digits');
            return;
        }
    }

        // Validate languageId (exactly 1 digit)
        if (name === 'languageId') {
            if (!/^\d{0,1}$/.test(value)) {
                setMessage('Language ID must be exactly 1 digit (1-3)');
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setIsDirty(true);
        setIsSubmitted(false);
        setMessage('');
    };
    
    const validateImage = (file, type, field) => {
        if (!file) {
            setErrors(prev => ({ ...prev, [field]: `Please select ${type}` }));
            return false;
        }
        
        if (file.size > MAX_FILE_SIZE) {
            setErrors(prev => ({
                ...prev,
                [field]: `${type} size (${Math.round(file.size/1024)}KB) exceeds maximum allowed size (250KB)`
            }));
            return false;
        }
        
        if (!file.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, [field]: `${type} must be an image file` }));
            return false;
        }
        
        // Clear error for this field if valid
        setErrors(prev => ({ ...prev, [field]: '' }));
        return true;
    };
    
    const handleFileChange = (setter, previewSetter, type, field) => (e) => {
        const file = e.target.files[0];
        if (file && validateImage(file, type, field)) {
            setter(file);
            const reader = new FileReader();
            reader.onload = (ev) => previewSetter(ev.target.result);
            reader.readAsDataURL(file);
            setIsDirty(true);
            setIsSubmitted(false);
        }
    };

    const handleLeftImageChange   = handleFileChange(setLeftImage, setLeftPreview, "Left Image", "leftImage");
    const handleLeftImage_1Change = handleFileChange(setLeftImage_1, setLeftPreview_1, "Left Image 1", "leftImage_1");
    const handleRightImageChange  = handleFileChange(setRightImage, setRightPreview, "Right Image", "rightImage");
    const handleRightImage_1Change= handleFileChange(setRightImage_1, setRightPreview_1, "Right Image 1", "rightImage_1");
    const handleNicLogoChange     = handleFileChange(setNicLogo, setNicLogoPreview, "NIC Logo", "nicLogo");

    const showImagePreview = (imageSrc, title) => {
        setPreviewImage(imageSrc);
        setPreviewTitle(title);
        setShowPreview(true);
    };
    
    const closePreview = () => {
        setShowPreview(false);
        setPreviewImage(null);
        setPreviewTitle('');
    };
    
    const validateForm = () => {
        if (!formData.stateName) {
            setMessage('Please enter State Name');
            return false;
        }
        
        if (!/^\d{0,2}$/.test(formData.stateCode)) {
        setMessage('Please enter a valid State Code');
        return false;
       }
        
        if (!/^\d{1}$/.test(formData.languageId) || formData.languageId < 1 || formData.languageId > 3) {
            setMessage('Please enter a valid Language ID (1-3)');
            return false;
        }
        
        if (!formData.heading1) {
            setMessage('Please enter Heading 1');
            return false;
        }
        
        if (!formData.heading2) {
            setMessage('Please enter Heading 2');
            return false;
        }
        
        if (!formData.heading3) {
            setMessage('Please enter Heading 3');
            return false;
        }
        
        return true;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setMessage('Submitting the details...');
        
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            data.append(key, formData[key]);
        });
        
        if (leftImage) data.append('leftImage', leftImage);
        if (leftImage_1) data.append('leftImage_1', leftImage_1);
        if (rightImage) data.append('rightImage', rightImage);
        if (rightImage_1) data.append('rightImage_1', rightImage_1);
        if (nicLogo) data.append('nicLogo', nicLogo);
        
        try {
            setLoading(true)
            const response = await axios.post(`${process.env.REACT_APP_URL}/api/banner`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setMessage(response.data);
            setIsSubmitted(true);
            setIsDirty(false);
            setErrors({
                leftImage: '',
                leftImage_1: '',
                rightImage: '',
                rightImage_1: '',
                nicLogo: ''
            }); // Clear all image errors on successful submission
        } catch (error) {
            if (error.response && error.response.status === 413) {
                setMessage('Error: File size too large. Maximum allowed size is 250KB per image.');
            } else {
                setMessage('Error: ' + (error.response?.data || error.message));
            }
        }
        setLoading(false)
    };
    
    const handleReset = () => {
        setFormData({
            stateCode: '',
            languageId: '',
            heading1: 'Aadhaar enabled Public Distribution System -AePDS',
            heading2: 'Food, Civil Supplies and Consumer Affairs Department',
            heading3: '',
            stateName: '',
            pageTitle: '',
            designedBy: '',
            deptUrl: '',
            copyright: ''
        });
        
        setLeftImage(null);
        setLeftImage_1(null);
        setRightImage(null);
        setRightImage_1(null);
        setNicLogo(null);
        setLeftPreview(null);
        setLeftPreview_1(null);
        setRightPreview(null);
        setRightPreview_1(null);
        setNicLogoPreview(null);
        setMessage('');
        setErrors({
            leftImage: '',
            leftImage_1: '',
            rightImage: '',
            rightImage_1: '',
            nicLogo: ''
        });
        setIsSubmitted(false);
        setIsDirty(false);
    };
    
    return (
        <div className="wrapper row3">
            <div id="container">
                <div align="center">
                    <br/><br/>
                    
                    <form onSubmit={handleSubmit} encType="multipart/form-data">
                        <table id="banner_form_table" align="center" width="50%">
                            <tr className="tableheader">
                                <td align="center" colSpan="2">
                                    <font face="verdrna" color="white">Banner Form</font>
                                </td>
                            </tr>
                            <tr className="tableheader">
                                <td align="center" colSpan="2">
                                    <font face="verdrna" color="white">Header</font>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    State Name:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="stateName" 
                                        value={formData.stateName}
                                        onChange={handleInputChange}
                                        onKeyPress={(e) => {
                                            if (!/^[A-Za-z\s]$/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    State Code:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text"
                                        name="stateCode" 
                                        value={formData.stateCode}
                                        onChange={handleInputChange}
                                        onKeyPress={(e) => {
                                            if (!/^[0-9]$/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength="2"
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Page Title:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="pageTitle" 
                                        value={formData.pageTitle}
                                        onChange={handleInputChange}
                                        onKeyPress={(e) => {
                                            if (!/^[A-Za-z\s]$/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Language ID:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text"
                                        name="languageId" 
                                        value={formData.languageId}
                                        onChange={handleInputChange}
                                        onKeyPress={(e) => {
                                            if (!/^[0-9]$/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength="1"
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Heading 1:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="heading1" 
                                        value={formData.heading1}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Heading 2:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="heading2" 
                                        value={formData.heading2}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Heading 3:<span style={{color: 'red'}}>*</span></td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="heading3" 
                                        value={formData.heading3}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Left Image  1: (Max 250KB)</td>
                                <td>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleLeftImageChange}
                                        
                                    />
                                    {errors.leftImage && <div style={{color: 'red'}}>{errors.leftImage}</div>}
                                    {leftPreview && (
                                        <div>
                                            <button 
                                                type="button" 
                                                className="preview-button"
                                                onClick={() => showImagePreview(leftPreview, 'Left Image Preview')}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Left Image 2: (Max 250KB)</td>
                                <td>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleLeftImage_1Change}
                                        
                                    />
                                    {errors.leftImage_1 && <div style={{color: 'red'}}>{errors.leftImage_1}</div>}
                                    {leftPreview_1 && (
                                        <div>
                                            <button 
                                                type="button" 
                                                className="preview-button"
                                                onClick={() => showImagePreview(leftPreview_1, 'Left Image 1 Preview')}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Right Image 1 : (Max 250KB)</td>
                                <td>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleRightImageChange}
                                        
                                    />
                                    {errors.rightImage && <div style={{color: 'red'}}>{errors.rightImage}</div>}
                                    {rightPreview && (
                                        <div>
                                            <button 
                                                type="button" 
                                                className="preview-button"
                                                onClick={() => showImagePreview(rightPreview, 'Right Image Preview')}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Right Image 2: (Max 250KB)</td>
                                <td>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleRightImage_1Change}
                                        
                                    />
                                    {errors.rightImage_1 && <div style={{color: 'red'}}>{errors.rightImage_1}</div>}
                                    {rightPreview_1 && (
                                        <div>
                                            <button 
                                                type="button" 
                                                className="preview-button"
                                                onClick={() => showImagePreview(rightPreview_1, 'Right Image 1 Preview')}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr className="tableheader">
                                <td align="center" colSpan="2" style={{fontSize: '16px'}} bgcolor="black">
                                    <font face="verdrna" color="white">Footer</font>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Designed & Developed for (Department):</td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="designedBy" 
                                        value={formData.designedBy}
                                        onChange={handleInputChange}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Department Website (URL):</td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="deptUrl" 
                                        value={formData.deptUrl}
                                        onChange={handleInputChange}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Copyright:</td>
                                <td>
                                    <input 
                                        type="text" 
                                        name="copyright" 
                                        value={formData.copyright}
                                        onChange={handleInputChange}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <br/>
                                    NIC Logo: (Max 250KB)</td>
                                <td>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleNicLogoChange}
                                        
                                    />
                                    {errors.nicLogo && <div style={{color: 'red'}}>{errors.nicLogo}</div>}
                                    {nicLogoPreview && (
                                        <div>
                                            <button 
                                                type="button" 
                                                className="preview-button"
                                                onClick={() => showImagePreview(nicLogoPreview, 'NIC Logo Preview')}
                                            >
                                                Preview
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td align="center" colSpan={2}>
                                    <input type="submit" value="Submit" className="button button2" disabled={isSubmitted && !isDirty} title={isSubmitted && !isDirty ? "Data Submitted" : ""}/>
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    <input type="button" value="Reset" className="button button2" onClick={handleReset}/>
                                </td>
                            </tr>
                        </table>
                    </form>
                <br/><br/>
                {loading &&
            <div style={{display:'flex', justifyContent:'center',alignItems:'center'}}><Box sx={{ display: 'flex'}}>
            <CircularProgress />
        </Box></div>
    } 
                {!loading && message && <div className="mainhead"style={{color: 'red'}}>{message}</div>}   
                </div>
                <br/><br/>
                <br/><br/>
                <br/><br/>
                <br/><br/>
            </div>
            
            {showPreview && (
                <div className="modal-overlay" onClick={closePreview}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{previewTitle}</h3>
                            <span className="close-button" onClick={closePreview}>&times;</span>
                        </div>
                        <div className="modal-body">
                            <img src={previewImage} alt="Preview" className="preview-image" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannerForm;